import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { TextField, TableContainer, Table, TableHead, TableRow, TableCell, Paper, TableBody, IconButton, Button, Grid } from '@mui/material';
import { Close, RestartAlt } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { translate } from '../../i18n/customI18nProvider'; 
import config from '../../config'; 
import { findClientInAnyEventByWristbandUID } from '../../services/client.service';

const UidReader = forwardRef(({ onWristbandsChange, onMounted }, ref) => {
    const [currentUid, setCurrentUid] = useState('');
    const [wristbands, setWristbands] = useState([]);
    const UID_LENGTH = 14;

    useImperativeHandle(ref, () => ({
        resetList: () => {
            setWristbands([]);
            notifyParent([]);
        },
        setBaseWristbands(baseWristbands) {
            setWristbands(baseWristbands);
            notifyParent(baseWristbands);
        },
        getWristbands: () => wristbands
    }));

    const notifyParent = (newList) => {
        if (onWristbandsChange) {
            onWristbandsChange(newList);
        }
    };

    useEffect(() => {
        if (onMounted) onMounted();
    }, []);

    const handleKeyDown = async (e) => {
        if (e.key === 'Enter') {
            const scannedUid = currentUid;
            
            if (wristbands.find(w => w.uid === scannedUid) || scannedUid.length !== UID_LENGTH) {
                setCurrentUid('');
                return;
            }

            try {
                const response = await findClientInAnyEventByWristbandUID(scannedUid);
                
                if (response.error) {
                    Swal.fire({
                        icon: 'error',
                        title: translate('custom.error'),
                        text: response.error,
                        confirmButtonColor: config.baseColor
                    });
                    setCurrentUid('');
                    return;
                }

                let status = 'non_activated';
                
                for (const client of response.clients) {
                    if (!client.archived) {
                        status = 'activated';
                        await Swal.fire({
                            icon: 'error',
                            title: translate('custom.client_still_active'),
                            html: translate('custom.client_is_not_archived_for_the_wristband') + ' ' + '<a target="_blank" href="/' + scannedUid + '">' + scannedUid + '</a>',
                            confirmButtonColor: config.baseColor
                        });
                        setCurrentUid('');
                        return;
                    } else {
                        status = 'archived';
                    }
                }

                const newWristband = { uid: scannedUid, status: status };
                const newList = [newWristband, ...wristbands];
                
                setWristbands(newList);
                notifyParent(newList);
                setCurrentUid('');

            } catch (error) {
                console.error(error);
                setCurrentUid('');
            }
        }
    };

    const handleRemoveWristband = (index) => {
        const newList = [...wristbands];
        newList.splice(index, 1);
        setWristbands(newList);
        notifyParent(newList);
    };

    const handleReset = async () => {
        const confirm_reset = await Swal.fire({
            icon: 'warning',
            title: translate('custom.warning'),
            text: translate('custom.are_you_sure_you_want_to_empty_wristbands_table'),
            showCancelButton: true,
            confirmButtonText: translate('custom.reset'),
            cancelButtonText: translate('custom.cancel'),
            confirmButtonColor: config.baseColor
        });
        if (confirm_reset.isConfirmed) {
            setWristbands([]);
            notifyParent([]);
        }
    };

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={8}>
                    <TextField 
                        style={{width: '100%'}} 
                        onKeyDown={handleKeyDown} 
                        type="text" 
                        label={translate('custom.uid')} 
                        value={currentUid} 
                        onChange={(e) => setCurrentUid((e.target.value).toUpperCase())} 
                        autoFocus
                    />
                </Grid>
                <Grid item xs={4}>
                     <Button 
                        startIcon={<RestartAlt />} 
                        variant="outlined" 
                        color="primary" 
                        onClick={handleReset} 
                        style={{width: '100%', height: '100%'}}
                        disabled={wristbands.length === 0}
                    >
                        {translate('custom.reset')}
                    </Button>
                </Grid>
            </Grid>
            
            <TableContainer component={Paper} style={{marginTop: '20px', maxHeight: '50vh', overflow: 'auto'}}>
                <Table stickyHeader sx={{ minWidth: 650 }} size="small" aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('custom.number')}</TableCell>
                            <TableCell>{translate('custom.uid')}</TableCell>
                            <TableCell>{translate('custom.status')}</TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {wristbands.length > 0 ? 
                            wristbands.map((wristband, index) => (
                                <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell component="th" scope="row">
                                        {wristbands.length - index}
                                    </TableCell>
                                    <TableCell component="th" scope="row">
                                        {wristband.uid}
                                    </TableCell>
                                    <TableCell style={{color: wristband.status === 'archived' ? 'green' : 'orange'}}>
                                        {translate("custom.wristband_status_" + wristband.status)}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => handleRemoveWristband(index)} style={{padding: '0px'}}>
                                            <Close />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            )) 
                        :
                            <TableRow>
                                <TableCell colSpan={4} align="center" style={{height: '60px', verticalAlign: 'middle'}}>
                                    {translate('custom.no_wristbands_scanned')}
                                </TableCell>
                            </TableRow>
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
});

export default UidReader;